import { useEffect, useMemo, useState } from "react";
import { OnboardingFeatureHighlightsPage } from "pages/onboarding/feature-highlights.page";
import { GoPaperlessPage } from "pages/onboarding/go-paperless.page";
import { JourneyDisclaimerPage } from "pages/onboarding/journey-disclaimer.page";
import { OnboardingCompletePage } from "pages/onboarding/onboarding-complete.page";
import { useFtuxCompletion } from "hooks/use-ftux-completion/use-ftux-completion.hook";
import { useJourneyNavEnabled } from "hooks/use-journey-nav-enabled/use-journey-nav-enabled.hook";

type StepFlags = {
  featureHighlights: boolean;
  goPaperless: boolean;
  journeyDisclaimer: boolean;
  onboardingComplete: boolean;
};

const ALL_COMPLETE: StepFlags = {
  featureHighlights: true,
  goPaperless: true,
  journeyDisclaimer: true,
  onboardingComplete: true,
};

const NONE_COMPLETE: StepFlags = {
  featureHighlights: false,
  goPaperless: false,
  journeyDisclaimer: false,
  onboardingComplete: false,
};

export const useOnboarding = () => {
  const {
    ftuxComplete,
    isLoading: ftuxLoading,
    error,
    markFtuxComplete,
  } = useFtuxCompletion();
  const { enabled: journeyNavEnabled, isLoading: journeyNavLoading } =
    useJourneyNavEnabled();

  const [stepsCompleted, setStepsCompleted] = useState<StepFlags>(ALL_COMPLETE);

  useEffect(() => {
    if (ftuxComplete === undefined) return;
    setStepsCompleted(ftuxComplete ? ALL_COMPLETE : NONE_COMPLETE);
  }, [ftuxComplete]);

  // Skip the AllSet screen when journey nav is off, but still mark FTUX
  // complete silently so the user doesn't re-enter onboarding on the next
  // session.
  useEffect(() => {
    if (
      !journeyNavEnabled &&
      !journeyNavLoading &&
      stepsCompleted.featureHighlights &&
      stepsCompleted.goPaperless &&
      !stepsCompleted.onboardingComplete
    ) {
      markFtuxComplete().catch(() => {
        // The local gate has already flipped, so a transient BE failure
        // shouldn't pin the user on the onboarding screen. Next session the
        // server-side `ftuxComplete` recomputes.
      });
      setStepsCompleted((s) => ({ ...s, onboardingComplete: true }));
    }
  }, [
    journeyNavEnabled,
    journeyNavLoading,
    stepsCompleted.featureHighlights,
    stepsCompleted.goPaperless,
    stepsCompleted.onboardingComplete,
    markFtuxComplete,
  ]);

  const steps = useMemo(
    () => [
      {
        isComplete: stepsCompleted.featureHighlights,
        component: (
          <OnboardingFeatureHighlightsPage
            onOnboardingComplete={() =>
              setStepsCompleted((s) => ({ ...s, featureHighlights: true }))
            }
          />
        ),
      },
      {
        isComplete: stepsCompleted.goPaperless,
        component: (
          <GoPaperlessPage
            onContinue={() =>
              setStepsCompleted((s) => ({ ...s, goPaperless: true }))
            }
          />
        ),
      },
      ...(journeyNavEnabled
        ? [
            {
              isComplete: stepsCompleted.journeyDisclaimer,
              component: (
                <JourneyDisclaimerPage
                  onContinue={() =>
                    setStepsCompleted((s) => ({
                      ...s,
                      journeyDisclaimer: true,
                    }))
                  }
                />
              ),
            },
          ]
        : []),
      ...(journeyNavEnabled
        ? [
            {
              isComplete: stepsCompleted.onboardingComplete,
              component: (
                <OnboardingCompletePage
                  onComplete={async () => {
                    await markFtuxComplete();
                    setStepsCompleted((s) => ({
                      ...s,
                      onboardingComplete: true,
                    }));
                  }}
                />
              ),
            },
          ]
        : []),
    ],
    [stepsCompleted, markFtuxComplete, journeyNavEnabled]
  );

  const currentStep = useMemo(() => steps.find((s) => !s.isComplete), [steps]);

  return {
    currentStep,
    isOnboardingComplete: !currentStep,
    isLoading: ftuxLoading || journeyNavLoading,
    error,
  };
};
